/**
 * UID-59 — decimal-safe money helpers for the Cart screen.
 *
 * `Price.amount` on the wire is a decimal STRING (ADR-0004: money must be
 * exact, a JSON number risks float rounding). These helpers do the same:
 * every arithmetic step goes through integer cents, never a bare `*`/`+` on
 * the decimal strings or a float accumulator, so repeated display-side sums
 * can't drift from what the server would compute.
 *
 * DISPLAY ONLY. The Cart contract carries no `total`/`subtotal` field — the
 * frozen ADR-0029 rule (`total = Σ(line.price×qty) + Σ(charge.amount)`) is
 * documented on the contract as something every consumer re-derives, not a
 * field the server sends. This module is that re-derivation for the Cart
 * screen; it is never the authoritative total (checkout/order placement
 * re-derives it server-side, same rule).
 */

/** Parse a decimal-string money amount into integer cents. Throws on a
 * non-finite or malformed amount rather than silently coercing to 0 — a
 * broken Price should fail loudly, not render as "$0.00". */
export function centsFromAmount(amount: string): number {
  const value = Number(amount)
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid money amount: "${amount}"`)
  }
  return Math.round(value * 100)
}

/** Integer cents back to a decimal-string amount, e.g. 123456 -> "1234.56". */
export function amountFromCents(cents: number): string {
  const negative = cents < 0
  const abs = Math.round(Math.abs(cents))
  const whole = Math.floor(abs / 100)
  const frac = (abs % 100).toString().padStart(2, '0')
  return `${negative ? '-' : ''}${whole}.${frac}`
}

/** `amount × quantity`, e.g. line price x quantity.value (ADR-0029). */
export function multiplyAmount(amount: string, quantity: number): string {
  return amountFromCents(Math.round(centsFromAmount(amount) * quantity))
}

/** Sum of decimal-string amounts, each via integer cents (never a float accumulator). */
export function addAmounts(amounts: readonly string[]): string {
  const totalCents = amounts.reduce((sum, amount) => sum + centsFromAmount(amount), 0)
  return amountFromCents(totalCents)
}

/** Locale-formatted currency string for display, e.g. "1234.56" -> "$1,234.56". */
export function formatMoney(amount: string, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount))
}
